import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft, ClipboardList, Building2, Video, CalendarDays, DollarSign, Camera,
  Plus, Edit3, Trash2, X, MapPin, Phone, Mail, ToggleLeft, ToggleRight,
  CheckCircle2, Download, RefreshCw, UserCheck, UserX, Briefcase, AlertTriangle, Eye,
} from "lucide-react";

/* ───────────────────────────── Tab buttons ───────────────────────────── */
const TabButton = ({ active, onClick, icon: Icon, label, testid, badge }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    style={{
      flex: '0 0 auto', padding: '10px 16px', cursor: 'pointer',
      background: active ? 'rgba(0,229,255,0.12)' : 'transparent',
      border: active ? '1px solid rgba(0,229,255,0.5)' : '1px solid transparent',
      borderBottom: active ? '2px solid var(--cyan)' : '2px solid transparent',
      borderRadius: 10, color: active ? 'var(--cyan)' : 'var(--text-secondary)',
      fontSize: 13, fontWeight: 700, fontFamily: "'Outfit', sans-serif",
      display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap',
    }}>
    <Icon size={14} /> {label}
    {badge != null && (
      <span style={{
        background: active ? 'var(--cyan)' : 'rgba(255,255,255,0.1)',
        color: active ? '#000' : '#bfc7d4',
        padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 800,
      }}>{badge}</span>
    )}
  </button>
);

/* ───────────────────────────── ORGS CRM TAB ───────────────────────────── */
const OrgsCRMTab = ({ api }) => {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null); // org id

  const refresh = useCallback(() => {
    setLoading(true);
    api().get("/ysv-logistics/orgs").then(r => setOrgs(r.data)).finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { refresh(); }, [refresh]);

  // Group orgs by league name so the CRM tab reads like a directory:
  // "League Name" header, then a magazine-grid of that league's orgs.
  const groupedByLeague = useMemo(() => {
    const map = new Map();
    for (const o of orgs) {
      const key = o.league_name || "Unassigned";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(o);
    }
    // Stable alphabetical, but push "Unassigned" to the end
    return Array.from(map.entries()).sort(([a], [b]) => {
      if (a === "Unassigned") return 1;
      if (b === "Unassigned") return -1;
      return a.localeCompare(b);
    });
  }, [orgs]);

  if (loading) return <p style={{ color: '#888', textAlign: 'center', padding: 40 }}>Loading…</p>;

  return (
    <div data-testid="ysv-logistics-orgs-tab">
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 18 }}>
        {orgs.length} organization{orgs.length === 1 ? '' : 's'} across {groupedByLeague.length} league{groupedByLeague.length === 1 ? '' : 's'} · View contacts, edit CRM info, see games recorded.
      </p>
      {groupedByLeague.map(([leagueName, leagueOrgs]) => (
        <div key={leagueName} data-testid={`league-section-${leagueName.replace(/\s+/g, '-')}`} style={{ marginBottom: 28 }}>
          <div style={{
            display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12,
            paddingBottom: 6, borderBottom: '1px solid #1a1a1e',
          }}>
            <h3 style={{
              fontSize: 13, fontWeight: 800, color: 'var(--cyan)',
              textTransform: 'uppercase', letterSpacing: 1.5, fontFamily: "'Outfit', sans-serif",
            }}>{leagueName}</h3>
            <span style={{ fontSize: 10, color: '#666' }}>· {leagueOrgs.length} org{leagueOrgs.length === 1 ? '' : 's'}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
            {leagueOrgs.map(o => (
              <div key={o.id} data-testid={`org-card-${o.id}`} className="glass-card" style={{ padding: 14 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h4 style={{ fontSize: 14, fontWeight: 800, color: '#fff', fontFamily: "'Outfit', sans-serif", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.name}</h4>
                    <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 10, color: '#ff6b00', fontWeight: 700 }}>{o.team_count} teams</span>
                      <span style={{ fontSize: 10, color: '#888' }}>·</span>
                      <span style={{ fontSize: 10, color: 'var(--cyan)', fontWeight: 700 }}>{o.player_count} players</span>
                      {(o.contact_count || 0) > 0 && (
                        <>
                          <span style={{ fontSize: 10, color: '#888' }}>·</span>
                          <span style={{ fontSize: 10, color: '#4caf50', fontWeight: 700 }}>{o.contact_count} contact{o.contact_count === 1 ? '' : 's'}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {o.home_field_address && (
                  <p style={{ fontSize: 11, color: '#bfc7d4', marginBottom: 8, display: 'flex', gap: 4, alignItems: 'flex-start' }}>
                    <MapPin size={11} style={{ marginTop: 2, color: '#888', flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.home_field_address}</span>
                  </p>
                )}
                <div style={{ display: 'flex', gap: 6 }}>
                  <button data-testid={`org-view-${o.id}`} onClick={() => setViewing(o.id)} style={{
                    flex: 1, background: 'rgba(0,229,255,0.1)', border: '1px solid rgba(0,229,255,0.3)', borderRadius: 8,
                    padding: '6px 10px', color: 'var(--cyan)', cursor: 'pointer', fontSize: 11, fontWeight: 700,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                  }}><Eye size={12} /> View</button>
                  <button data-testid={`org-edit-${o.id}`} onClick={() => setEditing(o)} style={{
                    flex: 1, background: 'transparent', border: '1px solid #2a3a4a', borderRadius: 8,
                    padding: '6px 10px', color: '#bfc7d4', cursor: 'pointer', fontSize: 11, fontWeight: 700,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                  }}><Edit3 size={12} /> Edit</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      {viewing && (
        <OrgCRMViewModal
          orgId={viewing}
          api={api}
          onClose={() => setViewing(null)}
          onEdit={() => {
            const o = orgs.find(x => x.id === viewing);
            setViewing(null);
            if (o) setEditing(o);
          }}
        />
      )}
      {editing && <OrgCRMEditModal org={editing} api={api} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
    </div>
  );
};

/* ─── View modal — shows everything; has "Edit" at top to switch to edit modal ─── */
const OrgCRMViewModal = ({ orgId, api, onClose, onEdit }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api().get(`/ysv-logistics/orgs/${orgId}`).then(r => setData(r.data)).finally(() => setLoading(false));
  }, [api, orgId]);

  const CONTACT_TYPE_LABEL = {
    primary: "President",   // backend stores the president as the org's "primary" contact
    org_admin: "Org Admin",
    coach: "Coach",
    team_mom: "Team Mom",
  };

  const contactsByType = useMemo(() => {
    if (!data?.contacts) return {};
    const map = {};
    for (const c of data.contacts) {
      if (!map[c.type]) map[c.type] = [];
      map[c.type].push(c);
    }
    return map;
  }, [data]);

  return (
    <div data-testid="org-view-modal" onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 2000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div className="glass-card" style={{ padding: 22, width: 'min(96vw, 580px)', maxHeight: '90vh', overflowY: 'auto' }}>
        {loading || !data ? (
          <p style={{ color: '#888', textAlign: 'center', padding: 30 }}>Loading…</p>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{ fontSize: 19, fontWeight: 800, color: '#fff', fontFamily: "'Outfit', sans-serif" }}>{data.name}</h3>
                <p style={{ fontSize: 11, color: 'var(--cyan)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginTop: 2 }}>{data.league_name || 'Unassigned'}</p>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button data-testid="org-view-edit-btn" onClick={onEdit} style={{
                  background: 'var(--cyan)', border: 'none', borderRadius: 8,
                  padding: '6px 12px', color: '#000', cursor: 'pointer', fontSize: 12, fontWeight: 800,
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}><Edit3 size={12} /> Edit</button>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer' }}><X size={20} /></button>
              </div>
            </div>

            {/* Stat row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 18 }}>
              <Stat label="Teams" value={data.team_count} color="#ff6b00" />
              <Stat label="Players" value={data.player_count} color="var(--cyan)" />
              <Stat label="Coaches" value={data.coach_count} color="#4caf50" />
              <Stat label="Games" value={data.total_games_recorded} color="#bb86fc" testid="org-view-games-count" />
            </div>

            {/* Home field */}
            {data.home_field_address && (
              <div style={{ marginBottom: 18 }}>
                <p style={{ fontSize: 10, color: 'var(--cyan)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Home Field</p>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', background: '#0f1217', borderRadius: 8 }}>
                  <MapPin size={14} style={{ color: '#888', marginTop: 2, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 12, color: '#bfc7d4' }}>{data.home_field_address}</p>
                    {(data.home_field_lat && data.home_field_lng) && (
                      <a
                        data-testid="org-view-maps-link"
                        href={`https://www.google.com/maps/search/?api=1&query=${data.home_field_lat},${data.home_field_lng}${data.home_field_place_id ? `&query_place_id=${data.home_field_place_id}` : ''}`}
                        target="_blank" rel="noreferrer"
                        style={{ fontSize: 11, color: 'var(--cyan)', textDecoration: 'underline', marginTop: 2, display: 'inline-block' }}>
                        Open in Google Maps →
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Contacts grouped by type */}
            <div style={{ marginBottom: 18 }}>
              <p style={{ fontSize: 10, color: 'var(--cyan)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                Contacts ({data.contacts?.length || 0})
              </p>
              {data.org_admin && (
                <ContactRow type="org_admin" name={data.org_admin.name} email={data.org_admin.email} phone={data.org_admin.phone} typeLabel="Org Admin (user)" />
              )}
              {["primary", "org_admin", "coach", "team_mom"].map(t =>
                (contactsByType[t] || []).map(c => (
                  <ContactRow
                    key={c.id}
                    type={t}
                    typeLabel={CONTACT_TYPE_LABEL[t]}
                    name={c.name}
                    email={c.email}
                    phone={c.phone}
                    teamName={c.team_name}
                  />
                ))
              )}
              {(data.contacts?.length || 0) === 0 && !data.org_admin && (
                <p style={{ fontSize: 11, color: '#666', textAlign: 'center', padding: 16, fontStyle: 'italic' }}>No contacts yet. Click Edit to add them.</p>
              )}
            </div>

            {/* Notes */}
            {data.notes && (
              <div style={{ marginBottom: 18 }}>
                <p style={{ fontSize: 10, color: 'var(--cyan)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Notes</p>
                <p style={{ fontSize: 12, color: '#bfc7d4', padding: '10px 12px', background: '#0f1217', borderRadius: 8, fontStyle: 'italic' }}>{data.notes}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, color, testid }) => (
  <div data-testid={testid} style={{ textAlign: 'center', padding: '10px 4px', background: `${color}11`, borderRadius: 8 }}>
    <p style={{ fontSize: 20, fontWeight: 800, color, fontFamily: "'Outfit', sans-serif" }}>{value}</p>
    <p style={{ fontSize: 9, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</p>
  </div>
);

const ContactRow = ({ type, typeLabel, name, email, phone, teamName }) => {
  const COLOR_MAP = { primary: '#bb86fc', org_admin: '#4caf50', coach: '#ff6b00', team_mom: 'var(--cyan)' };
  const c = COLOR_MAP[type] || '#888';
  return (
    <div style={{
      display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 10px', marginBottom: 6,
      background: '#0f1217', borderRadius: 8, borderLeft: `3px solid ${c}`,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 2 }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>{name}</p>
          <span style={{ fontSize: 9, color: c, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{typeLabel}</span>
        </div>
        {teamName && <p style={{ fontSize: 10, color: '#888' }}>Team: {teamName}</p>}
        {email && <p style={{ fontSize: 10, color: '#bfc7d4' }}><Mail size={9} style={{ verticalAlign: 'middle', marginRight: 3 }} />{email}</p>}
        {phone && <p style={{ fontSize: 10, color: '#bfc7d4' }}><Phone size={9} style={{ verticalAlign: 'middle', marginRight: 3 }} />{phone}</p>}
      </div>
    </div>
  );
};

const OrgCRMEditModal = ({ org, api, onClose, onSaved }) => {
  const [form, setForm] = useState({
    // President / team-mom etc. live in the contacts[] array now (the org no longer
    // carries president_* columns) — managed entirely by the contacts editor below.
    home_field_address: org.home_field_address || "",
    home_field_lat: org.home_field_lat ?? null,
    home_field_lng: org.home_field_lng ?? null,
    home_field_place_id: org.home_field_place_id || "",
    notes: org.notes || "",
    contacts: (org.contacts || []).map(c => ({ ...c })),
  });
  const [teams, setTeams] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Load teams scoped to this org so coach/team-mom contacts can attach to a team
    api().get("/teams").then(r => setTeams((r.data || []).filter(t => t.org_id === org.id))).catch(() => {});
  }, [api, org.id]);

  const addContact = () => {
    setForm(prev => ({
      ...prev,
      contacts: [...prev.contacts, { _draftId: Math.random().toString(36).slice(2), type: "primary", name: "", email: "", phone: "", team_id: null }],
    }));
  };

  const removeContact = (idx) => {
    setForm(prev => ({ ...prev, contacts: prev.contacts.filter((_, i) => i !== idx) }));
  };

  const updateContact = (idx, patch) => {
    setForm(prev => ({
      ...prev,
      contacts: prev.contacts.map((c, i) => i === idx ? { ...c, ...patch } : c),
    }));
  };

  const save = async () => {
    // Frontend validation: coach/team_mom must have a team_id
    for (const [i, c] of form.contacts.entries()) {
      if (!(c.name || "").trim()) {
        alert(`Contact #${i + 1}: name is required`); return;
      }
      if ((c.type === "coach" || c.type === "team_mom") && !c.team_id) {
        alert(`${c.type === "coach" ? "Coach" : "Team Mom"} (${c.name}) must be linked to a team.`); return;
      }
    }
    setSaving(true);
    try {
      // Strip draftId before sending; backend assigns real uuids
      const cleanedContacts = form.contacts.map(({ _draftId, team_name, ...rest }) => rest);
      await api().put(`/ysv-logistics/orgs/${org.id}/crm`, { ...form, contacts: cleanedContacts });
      onSaved();
    } catch (e) {
      alert(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const CONTACT_TYPES = [
    { k: "primary", l: "President" },   // backend type is "primary" (the org's primary contact)
    { k: "org_admin", l: "Org Admin" },
    { k: "coach", l: "Coach" },
    { k: "team_mom", l: "Team Mom" },
  ];

  return (
    <div data-testid="org-edit-modal" onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 2000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div className="glass-card" style={{ padding: 22, width: 'min(96vw, 580px)', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ fontSize: 17, fontWeight: 800, fontFamily: "'Outfit', sans-serif" }}>Edit CRM — {org.name}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer' }}><X size={20} /></button>
        </div>

        {/* Home field */}
        <p style={{ fontSize: 11, color: 'var(--cyan)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Home Field</p>
        <PlacesAutocompleteInput
          value={form.home_field_address}
          onChange={(addr, place) => setForm(prev => ({
            ...prev,
            home_field_address: addr,
            home_field_lat: place?.lat ?? prev.home_field_lat,
            home_field_lng: place?.lng ?? prev.home_field_lng,
            home_field_place_id: place?.place_id ?? prev.home_field_place_id,
          }))}
          placeholder="Search a field, park, or address…"
          testid="org-edit-home-field"
        />

        {/* Contacts editor */}
        <div style={{ marginTop: 16, marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <p style={{ fontSize: 11, color: 'var(--cyan)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Contacts ({form.contacts.length})
            </p>
            <button data-testid="org-edit-add-contact" onClick={addContact} type="button" style={{
              background: 'rgba(0,229,255,0.1)', border: '1px solid rgba(0,229,255,0.3)', borderRadius: 8,
              padding: '4px 10px', color: 'var(--cyan)', cursor: 'pointer', fontSize: 11, fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}><Plus size={11} /> Add Contact</button>
          </div>
          {form.contacts.length === 0 && (
            <p style={{ fontSize: 11, color: '#666', textAlign: 'center', padding: 12, fontStyle: 'italic', background: '#0f1217', borderRadius: 8 }}>
              No contacts yet. Click "Add Contact" to add a president, coach, team mom, etc.
            </p>
          )}
          {form.contacts.map((c, idx) => {
            const requiresTeam = c.type === "coach" || c.type === "team_mom";
            return (
              <div
                key={c.id || c._draftId || idx}
                data-testid={`org-edit-contact-${idx}`}
                style={{ background: '#0f1217', borderRadius: 10, padding: 10, marginBottom: 8, border: '1px solid #1a1a1e' }}>
                <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <select
                    data-testid={`org-edit-contact-type-${idx}`}
                    className="input-field"
                    value={c.type}
                    onChange={e => updateContact(idx, { type: e.target.value, team_id: (e.target.value === "coach" || e.target.value === "team_mom") ? c.team_id : null })}
                    style={{ flex: '0 0 120px' }}
                  >
                    {CONTACT_TYPES.map(t => <option key={t.k} value={t.k}>{t.l}</option>)}
                  </select>
                  <input
                    data-testid={`org-edit-contact-name-${idx}`}
                    className="input-field"
                    placeholder="Name"
                    value={c.name}
                    onChange={e => updateContact(idx, { name: e.target.value })}
                  />
                  <button type="button" onClick={() => removeContact(idx)} style={{
                    background: 'rgba(244,67,54,0.1)', color: '#f44336', border: '1px solid rgba(244,67,54,0.3)',
                    borderRadius: 8, padding: '0 10px', cursor: 'pointer', fontSize: 11,
                  }}><Trash2 size={12} /></button>
                </div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <input className="input-field" placeholder="Email" value={c.email || ""} onChange={e => updateContact(idx, { email: e.target.value })} />
                  <input className="input-field" placeholder="Phone" value={c.phone || ""} onChange={e => updateContact(idx, { phone: e.target.value })} />
                </div>
                {requiresTeam && (
                  <select
                    data-testid={`org-edit-contact-team-${idx}`}
                    className="input-field"
                    value={c.team_id || ""}
                    onChange={e => updateContact(idx, { team_id: e.target.value || null })}>
                    <option value="">— Select team —</option>
                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                )}
              </div>
            );
          })}
        </div>

        <p style={{ fontSize: 11, color: 'var(--cyan)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Notes</p>
        <textarea className="input-field" data-testid="org-edit-notes" rows={3} placeholder="Internal CRM notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={{ marginBottom: 14, resize: 'vertical' }} />

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} disabled={saving} style={{ flex: 1, padding: 12, background: 'transparent', border: '1px solid #2a3a4a', borderRadius: 10, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
          <button onClick={save} data-testid="org-edit-save" disabled={saving} style={{ flex: 2, padding: 12, background: 'var(--cyan)', border: 'none', borderRadius: 10, color: '#000', fontWeight: 800, cursor: 'pointer' }}>
            {saving ? "Saving…" : "Save CRM"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Google Maps Places autocomplete with free-text fallback ──────────── */
const PlacesAutocompleteInput = ({ value, onChange, placeholder, testid }) => {
  const [mapsKey, setMapsKey] = useState(null);
  const [internalValue, setInternalValue] = useState(value || "");
  const inputRef = React.useRef(null);
  const { api } = useAuth();

  useEffect(() => { setInternalValue(value || ""); }, [value]);

  useEffect(() => {
    api().get("/ysv-logistics/maps-key").then(r => setMapsKey(r.data.maps_api_key || "")).catch(() => setMapsKey(""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load Google Maps JS API once and attach Places autocomplete
  useEffect(() => {
    if (!mapsKey || !inputRef.current) return;
    const SCRIPT_ID = "ysv-google-maps-js";
    const attach = () => {
      if (!window.google?.maps?.places || !inputRef.current) return;
      const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
        fields: ["place_id", "geometry", "formatted_address", "name"],
      });
      ac.addListener("place_changed", () => {
        const p = ac.getPlace();
        const addr = p.formatted_address || p.name || inputRef.current.value;
        setInternalValue(addr);
        onChange(addr, {
          lat: p.geometry?.location?.lat?.(),
          lng: p.geometry?.location?.lng?.(),
          place_id: p.place_id,
        });
      });
    };
    if (window.google?.maps?.places) { attach(); return; }
    if (document.getElementById(SCRIPT_ID)) {
      const t = setInterval(() => {
        if (window.google?.maps?.places) { clearInterval(t); attach(); }
      }, 200);
      return () => clearInterval(t);
    }
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(mapsKey)}&libraries=places&loading=async`;
    s.async = true;
    s.defer = true;
    s.onload = attach;
    document.head.appendChild(s);
  }, [mapsKey, onChange]);

  if (mapsKey === null) {
    return <input className="input-field" placeholder="Loading…" disabled />;
  }

  return (
    <div>
      <input
        ref={inputRef}
        data-testid={testid}
        className="input-field"
        placeholder={placeholder || "Address"}
        value={internalValue}
        onChange={e => { setInternalValue(e.target.value); onChange(e.target.value, null); }}
      />
      {!mapsKey && (
        <p style={{ fontSize: 10, color: '#888', marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <AlertTriangle size={10} /> Google Maps key not configured — using free-text fallback. Add GOOGLE_MAPS_API_KEY to backend/.env for autocomplete.
        </p>
      )}
    </div>
  );
};

/* ───────────────────────────── SCHEDULES TAB ───────────────────────────── */
const SchedulesTab = ({ api }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedLeague, setExpandedLeague] = useState(null);
  const [expandedOrg, setExpandedOrg] = useState(null);

  const refresh = useCallback(() => {
    setLoading(true);
    api().get("/ysv-logistics/schedules/hierarchy").then(r => setData(r.data)).finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { refresh(); }, [refresh]);

  const assignGDO = async (scheduleId, gdoId) => {
    try {
      await api().post(`/schedules/${scheduleId}/assign-gdo`, { gdo_id: gdoId || null });
      refresh();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to assign");
    }
  };

  if (loading) return <p style={{ color: '#888', textAlign: 'center', padding: 40 }}>Loading…</p>;
  if (!data) return null;

  return (
    <div data-testid="ysv-logistics-schedules-tab">
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 18 }}>
        {data.leagues.length} league{data.leagues.length === 1 ? '' : 's'} · {data.active_gdos.length} active GDO{data.active_gdos.length === 1 ? '' : 's'} this week
      </p>
      {data.leagues.map(lg => (
        <div key={lg.id} data-testid={`league-row-${lg.id}`} className="glass-card" style={{ padding: 14, marginBottom: 10 }}>
          <button onClick={() => setExpandedLeague(expandedLeague === lg.id ? null : lg.id)} style={{
            width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          }}>
            <div>
              <h3 style={{ fontSize: 15, fontWeight: 800, color: '#fff', fontFamily: "'Outfit', sans-serif" }}>{lg.name}</h3>
              <p style={{ fontSize: 11, color: '#888' }}>{lg.org_count} orgs · {lg.team_count} teams · {lg.schedule_count} schedules</p>
            </div>
            <span style={{ color: '#888', fontSize: 18 }}>{expandedLeague === lg.id ? '▾' : '▸'}</span>
          </button>
          {expandedLeague === lg.id && lg.orgs.map(o => (
            <div key={o.id} style={{ marginTop: 10, marginLeft: 14, paddingLeft: 12, borderLeft: '2px solid #1a1a1e' }}>
              <button onClick={() => setExpandedOrg(expandedOrg === o.id ? null : o.id)} style={{
                width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0',
              }}>
                <div>
                  <p style={{ fontSize: 13, fontWeight: 700, color: '#bfc7d4' }}>{o.name}</p>
                  <p style={{ fontSize: 10, color: '#666' }}>{o.team_count} teams · {o.schedule_count} schedules</p>
                </div>
                <span style={{ color: '#666', fontSize: 14 }}>{expandedOrg === o.id ? '▾' : '▸'}</span>
              </button>
              {expandedOrg === o.id && o.teams.map(t => (
                <div key={t.id} style={{ marginTop: 8, marginLeft: 12, paddingLeft: 10, borderLeft: '2px solid #1a1a1e' }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: '#fff', marginBottom: 6 }}>{t.name}</p>
                  {t.schedules.length === 0 && <p style={{ fontSize: 11, color: '#666', fontStyle: 'italic', marginBottom: 8 }}>No schedules</p>}
                  {t.schedules.map(s => (
                    <div key={s.id} data-testid={`sched-row-${s.id}`} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                      padding: '6px 8px', marginBottom: 4, background: '#0f1217', borderRadius: 6,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 11, color: '#fff', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {(s.game_date || '').split('T')[0]} {s.game_time ? `· ${s.game_time}` : ''} · vs {s.away_team_name || '?'}
                        </p>
                        <p style={{ fontSize: 10, color: '#888' }}>{s.location || s.location_address || 'No location set'}</p>
                      </div>
                      <select
                        data-testid={`sched-gdo-${s.id}`}
                        value={s.assigned_gdo_id || ""}
                        onChange={e => assignGDO(s.id, e.target.value)}
                        style={{
                          background: '#1a1a1e', color: '#fff', border: '1px solid #2a3a4a',
                          borderRadius: 6, padding: '4px 8px', fontSize: 11, minWidth: 140,
                        }}>
                        <option value="">— Unassigned —</option>
                        {data.active_gdos.map(g => (
                          <option key={g.id} value={g.id}>{g.name}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

/* ───────────────────────────── GDOS TAB ───────────────────────────── */
const GDOsTab = ({ api }) => {
  const [gdos, setGdos] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    Promise.all([
      api().get("/ysv-logistics/gdos"),
      api().get("/organizations"),
      api().get("/leagues"),
    ]).then(([g, o, l]) => {
      setGdos(g.data); setOrgs(o.data); setLeagues(l.data);
    }).finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { refresh(); }, [refresh]);

  const toggleStatus = async (g) => {
    const next = g.weekly_status !== "active";
    try {
      await api().post(`/ysv-logistics/gdos/${g.id}/weekly-status`, { active: next });
      refresh();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const resetAll = async () => {
    if (!window.confirm("Reset ALL GDOs back to inactive? Each GDO will need to opt back in this week.")) return;
    try {
      const r = await api().post("/ysv-logistics/gdos/reset-weekly");
      alert(`Reset ${r.data.reset_count} GDOs to inactive.`);
      refresh();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api().delete(`/gdo/operators/${confirmDelete.id}`);
      setConfirmDelete(null);
      refresh();
    } catch (e) {
      alert(e.response?.data?.detail || "Delete failed");
    } finally { setDeleting(false); }
  };

  const filtered = gdos.filter(g => typeFilter === "all" || (g.gdo_type || "ysv_gdo") === typeFilter);

  if (loading) return <p style={{ color: '#888', textAlign: 'center', padding: 40 }}>Loading…</p>;

  return (
    <div data-testid="ysv-logistics-gdos-tab">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {gdos.length} GDOs · {gdos.filter(g => g.weekly_status === "active").length} active this week
        </p>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[{ k: "all", l: "All" }, { k: "ysv_gdo", l: "YSV-GDO" }, { k: "org_gdo", l: "Org-GDO" }].map(opt => (
            <button key={opt.k} data-testid={`gdo-filter-${opt.k}`} onClick={() => setTypeFilter(opt.k)} style={{
              padding: '4px 12px', borderRadius: 999, fontSize: 11, fontWeight: 700,
              background: typeFilter === opt.k ? 'var(--cyan)' : 'transparent',
              color: typeFilter === opt.k ? '#000' : '#bfc7d4',
              border: '1px solid ' + (typeFilter === opt.k ? 'var(--cyan)' : '#2a3a4a'), cursor: 'pointer',
            }}>{opt.l}</button>
          ))}
          <button data-testid="gdo-reset-weekly" onClick={resetAll} style={{
            padding: '4px 12px', borderRadius: 999, fontSize: 11, fontWeight: 700,
            background: 'rgba(255,152,0,0.1)', color: '#ff9800', border: '1px solid rgba(255,152,0,0.3)', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}><RefreshCw size={11} /> Reset Week</button>
          <button data-testid="gdo-create-btn" onClick={() => setCreating(true)} className="btn-primary" style={{
            padding: '4px 12px', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4,
          }}><Plus size={12} /> Add GDO</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 10 }}>
        {filtered.map(g => {
          const isActive = g.weekly_status === "active";
          const isOrg = (g.gdo_type || "ysv_gdo") === "org_gdo";
          return (
            <div key={g.id} data-testid={`gdo-card-${g.id}`} className="glass-card" style={{ padding: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 800, color: '#fff', fontFamily: "'Outfit', sans-serif" }}>{g.name}</h4>
                  <p style={{ fontSize: 10, color: '#888' }}>{g.phone} {g.email && `· ${g.email}`}</p>
                </div>
                <button data-testid={`gdo-toggle-status-${g.id}`} onClick={() => toggleStatus(g)} style={{
                  background: isActive ? 'rgba(76,175,80,0.15)' : 'rgba(120,120,120,0.1)',
                  color: isActive ? '#4caf50' : '#888',
                  border: '1px solid ' + (isActive ? 'rgba(76,175,80,0.4)' : '#333'),
                  borderRadius: 8, padding: '4px 10px', fontSize: 10, fontWeight: 800, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 4, textTransform: 'uppercase', letterSpacing: 0.5,
                }}>
                  {isActive ? <ToggleRight size={11} /> : <ToggleLeft size={11} />} {isActive ? "Active" : "Inactive"}
                </button>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                <span style={{
                  fontSize: 10, padding: '2px 8px', borderRadius: 8, fontWeight: 700,
                  background: isOrg ? 'rgba(255,107,0,0.12)' : 'rgba(0,229,255,0.12)',
                  color: isOrg ? '#ff6b00' : 'var(--cyan)',
                }}>{isOrg ? "ORG-GDO" : "YSV-GDO"}</span>
                {g.assigned_cameras && (
                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8, fontWeight: 700, background: '#1e2732', color: '#bfc7d4' }}>
                    <Camera size={9} style={{ verticalAlign: 'middle', marginRight: 2 }} />{g.assigned_cameras}
                  </span>
                )}
              </div>
              {isOrg && g.org_name && <p style={{ fontSize: 11, color: '#bfc7d4', marginBottom: 4 }}><Briefcase size={10} style={{ verticalAlign: 'middle', marginRight: 4 }} />{g.org_name}</p>}
              {g.league_names?.length > 0 && (
                <p style={{ fontSize: 11, color: '#bfc7d4', marginBottom: 6 }}>Leagues: {g.league_names.join(", ")}</p>
              )}
              <div style={{ display: 'flex', gap: 6 }}>
                <button data-testid={`gdo-edit-type-${g.id}`} onClick={() => setEditing(g)} style={{
                  background: 'rgba(0,229,255,0.08)', color: 'var(--cyan)', border: '1px solid rgba(0,229,255,0.25)',
                  borderRadius: 6, padding: '4px 8px', fontSize: 10, fontWeight: 700, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}><Edit3 size={10} /> Type & Leagues</button>
                <button data-testid={`gdo-delete-${g.id}`} onClick={() => setConfirmDelete(g)} style={{
                  background: 'rgba(244,67,54,0.08)', color: '#f44336', border: '1px solid rgba(244,67,54,0.25)',
                  borderRadius: 6, padding: '4px 8px', fontSize: 10, fontWeight: 700, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}><Trash2 size={10} /> Delete</button>
              </div>
            </div>
          );
        })}
      </div>
      {editing && <GDOTypeEditModal gdo={editing} orgs={orgs} leagues={leagues} api={api} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
      {creating && <GDOCreateModal orgs={orgs} leagues={leagues} api={api} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); refresh(); }} />}
      {confirmDelete && (
        <ConfirmModal
          title={`Remove ${confirmDelete.name}?`}
          message={`This permanently deletes the GDO account. They lose access immediately and any schedule assignments are cleared. This cannot be undone.`}
          confirmText={deleting ? "Deleting…" : "Remove GDO"}
          confirmColor="#f44336"
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(null)}
          busy={deleting}
          testid="gdo-delete-confirm"
        />
      )}
    </div>
  );
};

const ConfirmModal = ({ title, message, confirmText, confirmColor, onConfirm, onCancel, busy, testid }) => (
  <div onClick={e => e.target === e.currentTarget && !busy && onCancel()} style={{
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 2200,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  }} data-testid={testid}>
    <div className="glass-card" style={{ padding: 22, width: 'min(94vw, 420px)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 999, background: 'rgba(244,67,54,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <AlertTriangle size={18} style={{ color: '#f44336' }} />
        </div>
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 800, color: '#fff', fontFamily: "'Outfit', sans-serif" }}>{title}</h3>
          <p style={{ fontSize: 12, color: '#bfc7d4', marginTop: 4, lineHeight: 1.5 }}>{message}</p>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button data-testid={`${testid}-cancel`} onClick={onCancel} disabled={busy} style={{
          flex: 1, padding: 11, background: 'transparent', border: '1px solid #2a3a4a',
          borderRadius: 10, color: '#fff', fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer',
        }}>Cancel</button>
        <button data-testid={`${testid}-confirm`} onClick={onConfirm} disabled={busy} style={{
          flex: 1, padding: 11, background: confirmColor, border: 'none',
          borderRadius: 10, color: '#fff', fontWeight: 800, cursor: busy ? 'wait' : 'pointer',
        }}>{confirmText}</button>
      </div>
    </div>
  </div>
);

const GDOCreateModal = ({ orgs, leagues, api, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", password: "",
    assigned_cameras: "",
    gdo_type: "ysv_gdo",
    gdo_org_id: "",
    assigned_league_ids: [],
  });
  const [saving, setSaving] = useState(false);

  const toggleLeague = (id) => {
    setForm(prev => ({
      ...prev,
      assigned_league_ids: prev.assigned_league_ids.includes(id)
        ? prev.assigned_league_ids.filter(x => x !== id)
        : [...prev.assigned_league_ids, id],
    }));
  };

  const save = async () => {
    if (!form.name.trim() || !form.phone.trim() || !form.password) {
      alert("Name, phone, and password are required"); return;
    }
    if (form.gdo_type === "org_gdo" && !form.gdo_org_id) {
      alert("Pick the organization for this Org-GDO"); return;
    }
    setSaving(true);
    try {
      await api().post("/gdo/operators", {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        password: form.password,
        assigned_cameras: form.assigned_cameras.trim(),
        gdo_type: form.gdo_type,
        gdo_org_id: form.gdo_type === "org_gdo" ? form.gdo_org_id : null,
        assigned_league_ids: form.assigned_league_ids,
        // Existing fields the backend still expects (left empty intentionally)
        assigned_team_ids: [],
        assigned_org_id: null,
      });
      onSaved();
    } catch (e) {
      alert(e.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 2000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} data-testid="gdo-create-modal">
      <div className="glass-card" style={{ padding: 22, width: 'min(96vw, 480px)', maxHeight: '92vh', overflowY: 'auto' }}>
        <h3 style={{ fontSize: 17, fontWeight: 800, marginBottom: 14, fontFamily: "'Outfit', sans-serif" }}>Add GDO</h3>
        <input className="input-field" data-testid="gdo-create-name" placeholder="Full name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={{ marginBottom: 8 }} />
        <input className="input-field" data-testid="gdo-create-phone" placeholder="Phone (login id)" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} style={{ marginBottom: 8 }} />
        <input className="input-field" data-testid="gdo-create-email" placeholder="Email (optional)" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={{ marginBottom: 8 }} />
        <input className="input-field" data-testid="gdo-create-password" type="text" placeholder="Initial password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} style={{ marginBottom: 8 }} />
        <input className="input-field" data-testid="gdo-create-cameras" placeholder="Assigned camera(s) e.g. XBOT-A1" value={form.assigned_cameras} onChange={e => setForm({ ...form, assigned_cameras: e.target.value })} style={{ marginBottom: 14 }} />

        <p style={{ fontSize: 11, color: 'var(--cyan)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Type</p>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {[{ k: "ysv_gdo", l: "YSV-GDO", d: "Works directly for YSV" }, { k: "org_gdo", l: "Org-GDO", d: "Contracted via an organization" }].map(opt => (
            <button key={opt.k} type="button" data-testid={`gdo-create-type-${opt.k}`} onClick={() => setForm({ ...form, gdo_type: opt.k })} style={{
              flex: 1, padding: 10, borderRadius: 10, textAlign: 'left',
              background: form.gdo_type === opt.k ? 'rgba(0,229,255,0.12)' : 'transparent',
              border: '1px solid ' + (form.gdo_type === opt.k ? 'rgba(0,229,255,0.5)' : '#2a3a4a'),
              cursor: 'pointer', color: '#fff',
            }}>
              <p style={{ fontSize: 12, fontWeight: 800 }}>{opt.l}</p>
              <p style={{ fontSize: 10, color: '#888', marginTop: 2 }}>{opt.d}</p>
            </button>
          ))}
        </div>

        {form.gdo_type === "org_gdo" && (
          <>
            <p style={{ fontSize: 11, color: 'var(--cyan)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Organization</p>
            <select className="input-field" data-testid="gdo-create-org-select" value={form.gdo_org_id} onChange={e => setForm({ ...form, gdo_org_id: e.target.value })} style={{ marginBottom: 14 }}>
              <option value="">— Select org —</option>
              {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </>
        )}

        <p style={{ fontSize: 11, color: 'var(--cyan)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Assigned Leagues</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
          {leagues.map(l => (
            <button key={l.id} type="button" data-testid={`gdo-create-league-${l.id}`} onClick={() => toggleLeague(l.id)} style={{
              padding: '5px 12px', borderRadius: 999, fontSize: 11, fontWeight: 700,
              background: form.assigned_league_ids.includes(l.id) ? 'var(--cyan)' : 'transparent',
              color: form.assigned_league_ids.includes(l.id) ? '#000' : '#bfc7d4',
              border: '1px solid ' + (form.assigned_league_ids.includes(l.id) ? 'var(--cyan)' : '#2a3a4a'),
              cursor: 'pointer',
            }}>{l.name}</button>
          ))}
          {leagues.length === 0 && (
            <p style={{ fontSize: 11, color: '#666', fontStyle: 'italic' }}>No leagues defined yet.</p>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} disabled={saving} style={{ flex: 1, padding: 12, background: 'transparent', border: '1px solid #2a3a4a', borderRadius: 10, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
          <button onClick={save} data-testid="gdo-create-save" disabled={saving} style={{ flex: 2, padding: 12, background: 'var(--cyan)', border: 'none', borderRadius: 10, color: '#000', fontWeight: 800, cursor: 'pointer' }}>
            {saving ? "Creating…" : "Create GDO"}
          </button>
        </div>
      </div>
    </div>
  );
};

const GDOTypeEditModal = ({ gdo, orgs, leagues, api, onClose, onSaved }) => {
  const [form, setForm] = useState({
    gdo_type: gdo.gdo_type || "ysv_gdo",
    gdo_org_id: gdo.gdo_org_id || "",
    assigned_league_ids: gdo.assigned_league_ids || [],
  });
  const [saving, setSaving] = useState(false);

  const toggleLeague = (id) => {
    setForm(prev => ({
      ...prev,
      assigned_league_ids: prev.assigned_league_ids.includes(id)
        ? prev.assigned_league_ids.filter(x => x !== id)
        : [...prev.assigned_league_ids, id],
    }));
  };

  const save = async () => {
    if (form.gdo_type === "org_gdo" && !form.gdo_org_id) {
      alert("Select an organization for org-GDO"); return;
    }
    setSaving(true);
    try {
      await api().put(`/ysv-logistics/gdos/${gdo.id}/type`, form);
      onSaved();
    } catch (e) { alert(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="gdo-type-modal" onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 2000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div className="glass-card" style={{ padding: 22, width: 'min(96vw, 460px)', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ fontSize: 17, fontWeight: 800, marginBottom: 14, fontFamily: "'Outfit', sans-serif" }}>{gdo.name} — GDO setup</h3>
        <p style={{ fontSize: 11, color: 'var(--cyan)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Type</p>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {[{ k: "ysv_gdo", l: "YSV-GDO", d: "Works directly for YSV" }, { k: "org_gdo", l: "Org-GDO", d: "Contracted via an organization" }].map(opt => (
            <button key={opt.k} data-testid={`gdo-type-pick-${opt.k}`} onClick={() => setForm({ ...form, gdo_type: opt.k })} style={{
              flex: 1, padding: 10, borderRadius: 10, textAlign: 'left',
              background: form.gdo_type === opt.k ? 'rgba(0,229,255,0.12)' : 'transparent',
              border: '1px solid ' + (form.gdo_type === opt.k ? 'rgba(0,229,255,0.5)' : '#2a3a4a'),
              cursor: 'pointer', color: '#fff',
            }}>
              <p style={{ fontSize: 12, fontWeight: 800 }}>{opt.l}</p>
              <p style={{ fontSize: 10, color: '#888', marginTop: 2 }}>{opt.d}</p>
            </button>
          ))}
        </div>
        {form.gdo_type === "org_gdo" && (
          <>
            <p style={{ fontSize: 11, color: 'var(--cyan)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Organization</p>
            <select className="input-field" data-testid="gdo-type-org-select" value={form.gdo_org_id} onChange={e => setForm({ ...form, gdo_org_id: e.target.value })} style={{ marginBottom: 14 }}>
              <option value="">— Select org —</option>
              {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </>
        )}
        <p style={{ fontSize: 11, color: 'var(--cyan)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Assigned Leagues (can work multiple)</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
          {leagues.map(l => (
            <button key={l.id} data-testid={`gdo-type-league-${l.id}`} onClick={() => toggleLeague(l.id)} style={{
              padding: '5px 12px', borderRadius: 999, fontSize: 11, fontWeight: 700,
              background: form.assigned_league_ids.includes(l.id) ? 'var(--cyan)' : 'transparent',
              color: form.assigned_league_ids.includes(l.id) ? '#000' : '#bfc7d4',
              border: '1px solid ' + (form.assigned_league_ids.includes(l.id) ? 'var(--cyan)' : '#2a3a4a'),
              cursor: 'pointer',
            }}>{l.name}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} disabled={saving} style={{ flex: 1, padding: 12, background: 'transparent', border: '1px solid #2a3a4a', borderRadius: 10, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
          <button onClick={save} data-testid="gdo-type-save" disabled={saving} style={{ flex: 2, padding: 12, background: 'var(--cyan)', border: 'none', borderRadius: 10, color: '#000', fontWeight: 800, cursor: 'pointer' }}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ───────────────────────────── EQUIPMENT TAB ───────────────────────────── */
const EquipmentTab = ({ api }) => {
  const [cameras, setCameras] = useState([]);
  const [gdos, setGdos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [actionCam, setActionCam] = useState(null); // {camera, action: 'checkout' | 'checkin'}

  const refresh = useCallback(() => {
    setLoading(true);
    Promise.all([
      api().get("/ysv-logistics/cameras"),
      api().get("/ysv-logistics/gdos"),
    ]).then(([c, g]) => {
      setCameras(c.data); setGdos(g.data);
    }).finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { refresh(); }, [refresh]);

  const colorFor = (status) => ({
    available: '#4caf50', checked_out: '#ff6b00', retained: '#00e5ff', maintenance: '#f44336',
  }[status] || '#888');

  if (loading) return <p style={{ color: '#888', textAlign: 'center', padding: 40 }}>Loading…</p>;

  return (
    <div data-testid="ysv-logistics-equipment-tab">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {cameras.length} cameras · {cameras.filter(c => c.status === "available").length} available · {cameras.filter(c => c.status === "checked_out").length} out · {cameras.filter(c => c.status === "retained").length} retained
        </p>
        <button data-testid="camera-create-btn" onClick={() => setCreating(true)} className="btn-primary" style={{ padding: '8px 16px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} /> Add Camera
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {cameras.map(c => (
          <div key={c.id} data-testid={`camera-card-${c.id}`} className="glass-card" style={{ padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <div>
                <h4 style={{ fontSize: 14, fontWeight: 800, color: '#fff', fontFamily: "'Outfit', sans-serif" }}>
                  <Camera size={14} style={{ verticalAlign: 'middle', marginRight: 6, color: colorFor(c.status) }} />
                  {c.name}
                </h4>
                {c.serial && <p style={{ fontSize: 10, color: '#888', fontFamily: 'monospace' }}>SN: {c.serial}</p>}
              </div>
              <span style={{
                fontSize: 9, padding: '2px 8px', borderRadius: 8, fontWeight: 800,
                background: `${colorFor(c.status)}22`, color: colorFor(c.status), textTransform: 'uppercase', letterSpacing: 0.5,
              }}>{c.status.replace('_', ' ')}</span>
            </div>
            {c.assigned_to_name && (
              <p style={{ fontSize: 11, color: '#bfc7d4', marginBottom: 4 }}>
                <UserCheck size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />Out to {c.assigned_to_name}
              </p>
            )}
            {c.assigned_for_week && (
              <p style={{ fontSize: 10, color: '#888', marginBottom: 8 }}>Week {c.assigned_for_week}</p>
            )}
            <div style={{ display: 'flex', gap: 6 }}>
              {c.status === "available" && (
                <button data-testid={`camera-checkout-${c.id}`} onClick={() => setActionCam({ camera: c, action: "checkout" })} style={{
                  flex: 1, padding: '6px', background: '#ff6b00', border: 'none', borderRadius: 6,
                  color: '#000', fontSize: 11, fontWeight: 800, cursor: 'pointer',
                }}>Check Out</button>
              )}
              {(c.status === "checked_out" || c.status === "retained") && (
                <button data-testid={`camera-checkin-${c.id}`} onClick={() => setActionCam({ camera: c, action: "checkin" })} style={{
                  flex: 1, padding: '6px', background: '#4caf50', border: 'none', borderRadius: 6,
                  color: '#000', fontSize: 11, fontWeight: 800, cursor: 'pointer',
                }}>Check In</button>
              )}
            </div>
          </div>
        ))}
        {cameras.length === 0 && (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: 40, color: '#888' }}>
            No cameras yet. Click "Add Camera" to register your first one.
          </div>
        )}
      </div>

      {creating && <CameraCreateModal api={api} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); refresh(); }} />}
      {actionCam && <CameraActionModal {...actionCam} gdos={gdos} api={api} onClose={() => setActionCam(null)} onSaved={() => { setActionCam(null); refresh(); }} />}
    </div>
  );
};

const CameraCreateModal = ({ api, onClose, onSaved }) => {
  const [form, setForm] = useState({ name: "", serial: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.name.trim()) { alert("Name required"); return; }
    setSaving(true);
    try {
      await api().post("/ysv-logistics/cameras", form);
      onSaved();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 2000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} data-testid="camera-create-modal">
      <div className="glass-card" style={{ padding: 22, width: 'min(96vw, 420px)' }}>
        <h3 style={{ fontSize: 17, fontWeight: 800, marginBottom: 14, fontFamily: "'Outfit', sans-serif" }}>Add Camera</h3>
        <input className="input-field" data-testid="camera-name" placeholder="Name (e.g. XBOT-A1)" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={{ marginBottom: 8 }} />
        <input className="input-field" data-testid="camera-serial" placeholder="Serial number (optional)" value={form.serial} onChange={e => setForm({ ...form, serial: e.target.value })} style={{ marginBottom: 8 }} />
        <textarea className="input-field" data-testid="camera-notes" rows={2} placeholder="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={{ marginBottom: 14, resize: 'vertical' }} />
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} disabled={saving} style={{ flex: 1, padding: 12, background: 'transparent', border: '1px solid #2a3a4a', borderRadius: 10, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
          <button onClick={save} data-testid="camera-create-save" disabled={saving} style={{ flex: 2, padding: 12, background: 'var(--cyan)', border: 'none', borderRadius: 10, color: '#000', fontWeight: 800, cursor: 'pointer' }}>
            {saving ? "Adding…" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
};

const isoWeek = () => {
  const d = new Date();
  const year = d.getFullYear();
  const start = new Date(year, 0, 1);
  const diff = (d - start) / 86400000;
  const week = Math.ceil((diff + start.getDay() + 1) / 7);
  return `${year}-W${String(week).padStart(2, '0')}`;
};

const CameraActionModal = ({ camera, action, gdos, api, onClose, onSaved }) => {
  const isCheckout = action === "checkout";
  const activeGdos = useMemo(() => gdos.filter(g => g.weekly_status === "active"), [gdos]);
  const [form, setForm] = useState(isCheckout
    ? { gdo_user_id: "", assigned_for_week: isoWeek(), notes: "" }
    : { status: "returned", games_recorded: 0, payment_amount: 0, notes: "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (isCheckout && !form.gdo_user_id) { alert("Pick a GDO"); return; }
    setSaving(true);
    try {
      const url = `/ysv-logistics/cameras/${camera.id}/${isCheckout ? "checkout" : "checkin"}`;
      await api().post(url, form);
      onSaved();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 2000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} data-testid={`camera-action-${action}-modal`}>
      <div className="glass-card" style={{ padding: 22, width: 'min(96vw, 460px)' }}>
        <h3 style={{ fontSize: 17, fontWeight: 800, marginBottom: 14, fontFamily: "'Outfit', sans-serif" }}>
          {isCheckout ? "Check Out" : "Check In"} — {camera.name}
        </h3>
        {isCheckout ? (
          <>
            <p style={{ fontSize: 11, color: 'var(--cyan)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>GDO (active only)</p>
            <select className="input-field" data-testid="checkout-gdo-select" value={form.gdo_user_id} onChange={e => setForm({ ...form, gdo_user_id: e.target.value })} style={{ marginBottom: 8 }}>
              <option value="">— Select GDO —</option>
              {activeGdos.map(g => <option key={g.id} value={g.id}>{g.name}{g.gdo_type === "org_gdo" ? " (org)" : ""}</option>)}
            </select>
            {activeGdos.length === 0 && (
              <p style={{ fontSize: 10, color: '#ff9800', marginBottom: 10, display: 'flex', gap: 4, alignItems: 'center' }}>
                <AlertTriangle size={10} /> No active GDOs — they must flip their weekly status before equipment can be assigned.
              </p>
            )}
            <input className="input-field" data-testid="checkout-week" placeholder="Week (YYYY-Wnn)" value={form.assigned_for_week} onChange={e => setForm({ ...form, assigned_for_week: e.target.value })} style={{ marginBottom: 8 }} />
            <textarea className="input-field" rows={2} placeholder="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={{ marginBottom: 14, resize: 'vertical' }} />
          </>
        ) : (
          <>
            <p style={{ fontSize: 11, color: 'var(--cyan)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Status</p>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              {[{ k: "returned", l: "Returned", c: "#4caf50" }, { k: "out", l: "Still Out", c: "#ff6b00" }, { k: "retained", l: "Retained", c: "var(--cyan)" }].map(opt => (
                <button key={opt.k} data-testid={`checkin-status-${opt.k}`} onClick={() => setForm({ ...form, status: opt.k })} style={{
                  flex: 1, padding: '8px', borderRadius: 8, fontSize: 11, fontWeight: 800, cursor: 'pointer',
                  background: form.status === opt.k ? opt.c : 'transparent',
                  color: form.status === opt.k ? '#000' : opt.c,
                  border: '1px solid ' + opt.c,
                }}>{opt.l}</button>
              ))}
            </div>
            <p style={{ fontSize: 11, color: 'var(--cyan)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Payroll (optional)</p>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input className="input-field" type="number" data-testid="checkin-games" min={0} placeholder="Games recorded" value={form.games_recorded} onChange={e => setForm({ ...form, games_recorded: Number(e.target.value) || 0 })} />
              <input className="input-field" type="number" step="0.01" data-testid="checkin-amount" min={0} placeholder="Amount $" value={form.payment_amount} onChange={e => setForm({ ...form, payment_amount: Number(e.target.value) || 0 })} />
            </div>
            <textarea className="input-field" rows={2} placeholder="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={{ marginBottom: 14, resize: 'vertical' }} />
            <p style={{ fontSize: 10, color: '#888', marginBottom: 10 }}>If games or amount &gt; 0, a payroll entry is created (visible in Payroll tab).</p>
          </>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} disabled={saving} style={{ flex: 1, padding: 12, background: 'transparent', border: '1px solid #2a3a4a', borderRadius: 10, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
          <button onClick={save} data-testid={`${action}-confirm`} disabled={saving} style={{ flex: 2, padding: 12, background: 'var(--cyan)', border: 'none', borderRadius: 10, color: '#000', fontWeight: 800, cursor: 'pointer' }}>
            {saving ? "Saving…" : (isCheckout ? "Check Out" : "Check In")}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ───────────────────────────── PAYROLL TAB ───────────────────────────── */
const PayrollTab = ({ api }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekFilter, setWeekFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const refresh = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (weekFilter) params.set("week", weekFilter);
    if (statusFilter) params.set("status", statusFilter);
    const q = params.toString() ? `?${params.toString()}` : "";
    api().get(`/ysv-logistics/payroll${q}`).then(r => setData(r.data)).finally(() => setLoading(false));
  }, [api, weekFilter, statusFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  const togglePaid = async (entry) => {
    const paid = entry.status !== "paid";
    const method = paid ? (prompt("Payment method (stripe/check/venmo/cash)?", "check") || "check") : "";
    try {
      await api().put(`/ysv-logistics/payroll/${entry.id}`, { paid, payment_method: method });
      refresh();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const downloadCSV = async () => {
    const params = new URLSearchParams();
    if (weekFilter) params.set("week", weekFilter);
    if (statusFilter) params.set("status", statusFilter);
    const q = params.toString() ? `?${params.toString()}` : "";
    const r = await api().get(`/ysv-logistics/payroll/export.csv${q}`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `ysv_payroll${weekFilter ? "_" + weekFilter : ""}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  if (loading) return <p style={{ color: '#888', textAlign: 'center', padding: 40 }}>Loading…</p>;
  if (!data) return null;

  return (
    <div data-testid="ysv-logistics-payroll-tab">
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="input-field" data-testid="payroll-week-filter" placeholder="Week filter e.g. 2026-W21" value={weekFilter} onChange={e => setWeekFilter(e.target.value)} style={{ flex: '0 0 220px' }} />
        <select className="input-field" data-testid="payroll-status-filter" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ flex: '0 0 160px' }}>
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
        </select>
        <button data-testid="payroll-csv-btn" onClick={downloadCSV} className="btn-primary" style={{ padding: '8px 14px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
        <div className="glass-card" style={{ padding: 14, textAlign: 'center' }}>
          <p style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', marginBottom: 4 }}>Pending</p>
          <p style={{ fontSize: 22, fontWeight: 800, color: '#ff6b00', fontFamily: "'Outfit', sans-serif" }}>${data.totals.pending_amount.toFixed(2)}</p>
        </div>
        <div className="glass-card" style={{ padding: 14, textAlign: 'center' }}>
          <p style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', marginBottom: 4 }}>Paid</p>
          <p style={{ fontSize: 22, fontWeight: 800, color: '#4caf50', fontFamily: "'Outfit', sans-serif" }}>${data.totals.paid_amount.toFixed(2)}</p>
        </div>
        <div className="glass-card" style={{ padding: 14, textAlign: 'center' }}>
          <p style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', marginBottom: 4 }}>Total ({data.totals.count})</p>
          <p style={{ fontSize: 22, fontWeight: 800, color: 'var(--cyan)', fontFamily: "'Outfit', sans-serif" }}>${data.totals.total_amount.toFixed(2)}</p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {data.entries.length === 0 && (
          <p style={{ textAlign: 'center', padding: 40, color: '#888' }}>No payroll entries — they appear automatically when a Hub Coordinator checks in a camera with games &gt; 0 or amount &gt; 0.</p>
        )}
        {data.entries.map(e => (
          <div key={e.id} data-testid={`payroll-row-${e.id}`} className="glass-card" style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{e.gdo_name} <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 6, marginLeft: 6, background: e.gdo_type === "org_gdo" ? 'rgba(255,107,0,0.12)' : 'rgba(0,229,255,0.12)', color: e.gdo_type === "org_gdo" ? '#ff6b00' : 'var(--cyan)', fontWeight: 700 }}>{e.gdo_type === "org_gdo" ? "ORG" : "YSV"}</span></p>
              <p style={{ fontSize: 10, color: '#888' }}>{e.week} · {e.camera_name || ''} · {e.games_recorded} game(s){e.payment_method ? ` · paid via ${e.payment_method}` : ''}</p>
            </div>
            <p style={{ fontSize: 16, fontWeight: 800, color: '#fff', fontFamily: "'Outfit', sans-serif", minWidth: 70, textAlign: 'right' }}>${(e.amount || 0).toFixed(2)}</p>
            <button data-testid={`payroll-toggle-${e.id}`} onClick={() => togglePaid(e)} style={{
              padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 800, cursor: 'pointer',
              background: e.status === "paid" ? 'rgba(76,175,80,0.15)' : 'rgba(255,107,0,0.15)',
              color: e.status === "paid" ? '#4caf50' : '#ff6b00',
              border: '1px solid ' + (e.status === "paid" ? 'rgba(76,175,80,0.4)' : 'rgba(255,107,0,0.4)'),
              textTransform: 'uppercase', letterSpacing: 0.5,
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
              {e.status === "paid" ? <><CheckCircle2 size={11} /> Paid</> : "Mark Paid"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ───────────────────────────── STAFF TAB (sub-accounts) ───────────── */
const StaffTab = ({ api, currentUser }) => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    api().get("/ysv-logistics/staff").then(r => setStaff(r.data)).finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { refresh(); }, [refresh]);

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name}? They lose access immediately.`)) return;
    try { await api().delete(`/ysv-logistics/staff/${s.id}`); refresh(); }
    catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  // Only true ysv_admin can manage staff sub-accounts
  if (currentUser.role !== "ysv_admin") {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>
        <AlertTriangle size={28} style={{ color: '#ff9800', marginBottom: 8 }} />
        <p>Only the YSV Admin can create or remove staff sub-accounts.</p>
      </div>
    );
  }

  if (loading) return <p style={{ color: '#888', textAlign: 'center', padding: 40 }}>Loading…</p>;

  const roleLabel = (r) => ({ league_coordinator: "League Coord.", hub_coordinator: "Hub Coord.", gdo_coordinator: "GDO Coord." }[r] || r);

  return (
    <div data-testid="ysv-logistics-staff-tab">
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14, alignItems: 'center' }}>
        <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{staff.length} staff sub-account{staff.length === 1 ? '' : 's'} — all inherit YSV Admin powers.</p>
        <button data-testid="staff-create-btn" onClick={() => setCreating(true)} className="btn-primary" style={{ padding: '8px 16px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} /> Add Staff
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {staff.map(s => (
          <div key={s.id} data-testid={`staff-card-${s.id}`} className="glass-card" style={{ padding: 14 }}>
            <h4 style={{ fontSize: 14, fontWeight: 800, color: '#fff', fontFamily: "'Outfit', sans-serif", marginBottom: 4 }}>{s.name}</h4>
            <p style={{ fontSize: 10, color: 'var(--cyan)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{roleLabel(s.role)}</p>
            <p style={{ fontSize: 11, color: '#bfc7d4' }}><Mail size={10} style={{ verticalAlign: 'middle', marginRight: 4 }} />{s.email}</p>
            {s.phone && <p style={{ fontSize: 11, color: '#bfc7d4', marginBottom: 8 }}><Phone size={10} style={{ verticalAlign: 'middle', marginRight: 4 }} />{s.phone}</p>}
            <button data-testid={`staff-remove-${s.id}`} onClick={() => remove(s)} style={{
              background: 'rgba(244,67,54,0.08)', color: '#f44336', border: '1px solid rgba(244,67,54,0.25)',
              borderRadius: 6, padding: '4px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}><Trash2 size={10} /> Remove</button>
          </div>
        ))}
        {staff.length === 0 && (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: 30, color: '#888' }}>
            No staff yet. Add a League / Hub / GDO Coordinator to give them YSV Admin powers.
          </div>
        )}
      </div>
      {creating && <StaffCreateModal api={api} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); refresh(); }} />}
    </div>
  );
};

const StaffCreateModal = ({ api, onClose, onSaved }) => {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "league_coordinator", phone: "", assigned_league_ids: [] });
  const [leagues, setLeagues] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api().get("/leagues").then(r => setLeagues(r.data)).catch(() => {}); }, [api]);

  const save = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password) { alert("Name, email, password required"); return; }
    setSaving(true);
    try {
      await api().post("/ysv-logistics/staff", form);
      onSaved();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const toggleLeague = (id) => {
    setForm(prev => ({ ...prev, assigned_league_ids: prev.assigned_league_ids.includes(id) ? prev.assigned_league_ids.filter(x => x !== id) : [...prev.assigned_league_ids, id] }));
  };

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 2000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} data-testid="staff-create-modal">
      <div className="glass-card" style={{ padding: 22, width: 'min(96vw, 460px)', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ fontSize: 17, fontWeight: 800, marginBottom: 14, fontFamily: "'Outfit', sans-serif" }}>Add Staff Sub-Account</h3>
        <select className="input-field" data-testid="staff-role" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} style={{ marginBottom: 8 }}>
          <option value="league_coordinator">League Coordinator</option>
          <option value="hub_coordinator">Hub Coordinator</option>
          <option value="gdo_coordinator">GDO Coordinator</option>
        </select>
        <input className="input-field" data-testid="staff-name" placeholder="Full name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={{ marginBottom: 8 }} />
        <input className="input-field" data-testid="staff-email" type="email" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={{ marginBottom: 8 }} />
        <input className="input-field" data-testid="staff-password" type="text" placeholder="Initial password (they should change after first login)" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} style={{ marginBottom: 8 }} />
        <input className="input-field" data-testid="staff-phone" placeholder="Phone (optional)" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} style={{ marginBottom: 14 }} />
        {form.role === "league_coordinator" && leagues.length > 0 && (
          <>
            <p style={{ fontSize: 11, color: 'var(--cyan)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Assigned Leagues</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
              {leagues.map(l => (
                <button key={l.id} type="button" onClick={() => toggleLeague(l.id)} style={{
                  padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                  background: form.assigned_league_ids.includes(l.id) ? 'var(--cyan)' : 'transparent',
                  color: form.assigned_league_ids.includes(l.id) ? '#000' : '#bfc7d4',
                  border: '1px solid ' + (form.assigned_league_ids.includes(l.id) ? 'var(--cyan)' : '#2a3a4a'), cursor: 'pointer',
                }}>{l.name}</button>
              ))}
            </div>
          </>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} disabled={saving} style={{ flex: 1, padding: 12, background: 'transparent', border: '1px solid #2a3a4a', borderRadius: 10, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
          <button onClick={save} data-testid="staff-create-save" disabled={saving} style={{ flex: 2, padding: 12, background: 'var(--cyan)', border: 'none', borderRadius: 10, color: '#000', fontWeight: 800, cursor: 'pointer' }}>
            {saving ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════ MAIN PAGE ════════════════════════════ */
const YSVLogisticsPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("schedules");

  useEffect(() => {
    if (!user) return;
    if (!["ysv_admin", "league_coordinator", "hub_coordinator", "gdo_coordinator"].includes(user.role)) {
      navigate("/");
    }
  }, [user, navigate]);

  if (!user) return null;

  return (
    <div data-testid="ysv-logistics-page" style={{ maxWidth: 1280, margin: '0 auto', padding: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <button data-testid="logistics-back-btn" onClick={() => navigate('/profile')} style={{
          background: 'rgba(0,229,255,0.08)', border: '1px solid rgba(0,229,255,0.25)',
          borderRadius: 999, padding: '6px 12px', color: 'var(--cyan)', cursor: 'pointer',
          fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4,
        }}><ArrowLeft size={12} /> Profile</button>
      </div>
      <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 4, fontFamily: "'Outfit', sans-serif", display: 'inline-flex', alignItems: 'center', gap: 10 }}>
        <ClipboardList size={22} style={{ color: 'var(--cyan)' }} /> YSV Logistics
      </h1>
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 18 }}>
        Schedules · CRM · GDO management · Equipment · Payroll
      </p>

      <div style={{ display: 'flex', gap: 6, marginBottom: 22, overflowX: 'auto', paddingBottom: 4 }}>
        <TabButton active={tab === "schedules"} onClick={() => setTab("schedules")} icon={CalendarDays} label="Schedules" testid="tab-schedules" />
        <TabButton active={tab === "orgs"} onClick={() => setTab("orgs")} icon={Building2} label="Orgs CRM" testid="tab-orgs" />
        <TabButton active={tab === "gdos"} onClick={() => setTab("gdos")} icon={Video} label="GDOs" testid="tab-gdos" />
        <TabButton active={tab === "equipment"} onClick={() => setTab("equipment")} icon={Camera} label="Equipment" testid="tab-equipment" />
        <TabButton active={tab === "payroll"} onClick={() => setTab("payroll")} icon={DollarSign} label="Payroll" testid="tab-payroll" />
        {user.role === "ysv_admin" && (
          <TabButton active={tab === "staff"} onClick={() => setTab("staff")} icon={UserCheck} label="Staff" testid="tab-staff" />
        )}
      </div>

      {tab === "schedules" && <SchedulesTab api={api} />}
      {tab === "orgs" && <OrgsCRMTab api={api} />}
      {tab === "gdos" && <GDOsTab api={api} />}
      {tab === "equipment" && <EquipmentTab api={api} />}
      {tab === "payroll" && <PayrollTab api={api} />}
      {tab === "staff" && <StaffTab api={api} currentUser={user} />}
    </div>
  );
};

export default YSVLogisticsPage;
